import { useTranslation } from 'react-i18next';
import { ContentLanguageCode } from '@/shared/constants';
import { getContentLanguageLabel } from '@/shared/utils/languageUtils';
import chevronIcon from '@/assets/svg/chevron_icon.svg';
import { ModalCloseButton } from '@/shared/components/ModalCloseButton';
import { DialogShell } from '@/shared/components/DialogShell';

interface LanguageModalProps {
    isOpen: boolean;
    onClose: () => void;
    selectedLanguage: ContentLanguageCode;
    onLanguageSelect: (lang: ContentLanguageCode) => void;
    activeLanguages: ContentLanguageCode[];
    museumLanguages: ContentLanguageCode[];
    onAddLanguage: (lang: ContentLanguageCode) => void;
    languageDropdownOpen: boolean;
    onLanguageDropdownToggle: () => void;
}

export const LanguageModal: React.FC<LanguageModalProps> = ({
    isOpen,
    onClose,
    selectedLanguage,
    onLanguageSelect,
    activeLanguages,
    museumLanguages,
    onAddLanguage,
    languageDropdownOpen,
    onLanguageDropdownToggle,
}) => {
    const { t } = useTranslation();
    const titleId = 'language-modal-title';

    const availableLanguages = museumLanguages
        .filter((code) => !activeLanguages.includes(code))
        .map((code) => [code, getContentLanguageLabel(code)] as const);

    return (
        <DialogShell
            isOpen={isOpen}
            onClose={onClose}
            titleId={titleId}
            overlayClassName="language-modal-overlay"
            dialogClassName={`language-modal ${languageDropdownOpen ? 'dropdown-open' : ''}`}
        >
            <div className="language-modal-header">
                <h2 id={titleId}>{t('exhibitions.chooseLanguage')}</h2>
                <ModalCloseButton onClick={onClose} className="language-modal-close" />
            </div>
            <div className="language-modal-content">
                <div className="language-dropdown">
                    <button
                        type="button"
                        className="language-dropdown-button"
                        onClick={onLanguageDropdownToggle}
                        aria-expanded={languageDropdownOpen}
                        aria-haspopup="listbox"
                    >
                        <span>{getContentLanguageLabel(selectedLanguage)}</span>
                        <img
                            src={chevronIcon}
                            alt=""
                            className={`chevron-icon ${languageDropdownOpen ? 'rotated' : ''}`}
                        />
                    </button>
                    {languageDropdownOpen && (
                        <div className="language-dropdown-menu" role="listbox">
                            {availableLanguages.length > 0 ? (
                                availableLanguages.map(([code, name]) => (
                                    <button
                                        key={code}
                                        type="button"
                                        role="option"
                                        aria-selected={selectedLanguage === code}
                                        onClick={() => {
                                            onLanguageSelect(code as ContentLanguageCode);
                                            onLanguageDropdownToggle();
                                        }}
                                        className={selectedLanguage === code ? 'active' : ''}
                                    >
                                        {name}
                                    </button>
                                ))
                            ) : (
                                <div className="no-languages-message">
                                    {t('exhibitions.allLanguagesAdded')}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
            <div className="language-modal-footer">
                <button
                    type="button"
                    className="language-modal-validate"
                    onClick={() => {
                        onAddLanguage(selectedLanguage);
                    }}
                    disabled={activeLanguages.includes(selectedLanguage) || availableLanguages.length === 0}
                >
                    {t('exhibitions.validate')}
                </button>
            </div>
        </DialogShell>
    );
};
